import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Share2,
  ArrowLeft,
  MessageSquare,
  ThumbsUp,
  ThumbsDown,
  Eye,
  Copy,
  Check,
  Clock,
  Film,
} from 'lucide-react';
import { apiClient } from '../lib/apiClient';
import { Video } from '../types/video';
import { useAuth } from '../hooks/useAuth';
import { useVideoReactions } from '../hooks/useVideoReactions';
import CustomVideoPlayer from './CustomVideoPlayer';
import Comments from './Comments';
import { toast } from '../lib/toast';
import { richTextToPlain } from '../lib/richText';

// ─── helpers ────────────────────────────────────────────────────────────────

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'https://nodebackendforacvrecording-1.onrender.com';

const BROWSER_TIME_ZONE =
  Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

const toAbsoluteMediaUrl = (value?: string): string => {
  if (!value || typeof value !== 'string') return '';
  if (/^https?:\/\//i.test(value) || value.startsWith('blob:') || value.startsWith('data:')) {
    return value;
  }
  if (value.startsWith('//')) {
    return `${window.location.protocol}${value}`;
  }
  const base = API_BASE_URL.replace(/\/+$/, '');
  const path = value.startsWith('/') ? value : `/${value}`;
  return `${base}${path}`;
};

const parseDurationSeconds = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value;
  if (typeof value !== 'string') return undefined;

  const trimmed = value.trim();
  if (!trimmed) return undefined;

  if (/^\d+:\d{1,2}(:\d{1,2})?$/.test(trimmed)) {
    const parts = trimmed.split(':').map((v) => Number(v));
    if (parts.some((v) => Number.isNaN(v))) return undefined;
    if (parts.length === 2) return parts[0] * 60 + parts[1];
    if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  }

  const asNumber = Number(trimmed);
  if (Number.isFinite(asNumber) && asNumber > 0) return asNumber;
  return undefined;
};

const formatDuration = (rawDuration?: unknown): string => {
  const seconds = parseDurationSeconds(rawDuration);
  if (!seconds) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const isPlaceholderDuration = (value: unknown): boolean => {
  const seconds = parseDurationSeconds(value);
  return !seconds || seconds === 60;
};

const resolveInitialDuration = (value: unknown): number | undefined => {
  return isPlaceholderDuration(value) ? undefined : parseDurationSeconds(value);
};

/** Loads duration from stream metadata even when a thumbnail image is shown. */
const DurationProbe: React.FC<{
  src: string;
  onDuration: (seconds: number) => void;
}> = ({ src, onDuration }) => {
  if (!src) return null;
  return (
    <video
      src={src}
      preload="metadata"
      className="hidden"
      muted
      playsInline
      aria-hidden="true"
      onLoadedMetadata={(e) => {
        const seconds = parseDurationSeconds((e.currentTarget as HTMLVideoElement).duration);
        if (seconds && !isPlaceholderDuration(seconds)) onDuration(seconds);
      }}
    />
  );
};

const persistVideoDuration = (videoId: string, seconds: number): void => {
  if (!videoId || isPlaceholderDuration(seconds)) return;
  void apiClient.updateVideoDuration(videoId, seconds);
};

const formatLocalDateTime = (dateStr?: string): string => {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString(navigator.language, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: BROWSER_TIME_ZONE,
    });
  } catch {
    return '';
  }
};

const buildShareUrl = (video: Video): string => {
  const token = video.shareToken || video.share_token;
  if (!token) return '';
  return `${window.location.origin}/share/${token}`;
};

// ─── VideoOwnerView ──────────────────────────────────────────────────────────

interface VideoOwnerViewProps {
  video: Video;
  onBack: () => void;
}

const VideoOwnerView: React.FC<VideoOwnerViewProps> = ({ video, onBack }) => {
  const { t } = useTranslation();
  const [commentCount, setCommentCount] = useState(0);
  const [showComments, setShowComments] = useState(true);
  /** The player's <video>: comments read its time and seek it. */
  const playerRef = useRef<HTMLVideoElement | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    apiClient.listComments(video.id).then(({ data }) => {
      if (data) setCommentCount(data.length);
    });
  }, [video.id]);

  const shareUrl = buildShareUrl(video);

  const { likes, dislikes, userReaction, toggleReaction } = useVideoReactions(video.id);

  const handleCopy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success(t('share_link_copied'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  const videoSrc = toAbsoluteMediaUrl(video.streamUrl || video.url || video.file_url);

  const handleDurationKnown = useCallback((seconds: number) => {
    persistVideoDuration(video.id, seconds);
  }, [video.id]);

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '0 16px 40px' }}>
      {/* Back + title row */}
      <div className="flex items-center gap-3 mb-4 pt-6">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-2 text-sm font-medium rounded-lg px-3 py-1.5 transition-colors"
          style={{
            color: 'var(--foreground, #111827)',
            background: 'var(--cardHoverBg, #f3f4f6)',
            border: '1px solid var(--border, #e5e7eb)',
          }}
        >
          <ArrowLeft className="w-4 h-4" aria-hidden="true" />
          {t('my_shares.back_to_list', 'My Shares')}
        </button>
      </div>

      {/* Player */}
      <div
        className="rounded-2xl overflow-hidden mb-5"
        style={{
          background: '#000',
          boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
          aspectRatio: '16 / 9',
          minHeight: 320,
          position: 'relative',
        }}
      >
        <CustomVideoPlayer
          src={videoSrc}
          className="absolute inset-0 w-full h-full"
          mimeType={video.mimeType || 'video/webm'}
          knownDuration={video.duration}
          videoId={video.id}
          onDurationKnown={handleDurationKnown}
          videoElementRef={playerRef}
        />
        {videoSrc ? <DurationProbe src={videoSrc} onDuration={handleDurationKnown} /> : null}
      </div>

      {/* Title + actions */}
      <div className="flex flex-col gap-3 mb-5">
        <h1
          className="text-xl font-bold"
          style={{ color: 'var(--cardTitleColor, var(--foreground, #111827))' }}
        >
          {video.title}
        </h1>

        <div className="flex flex-wrap items-center gap-3">
          {/* Reactions */}
          <button
            type="button"
            onClick={() => toggleReaction('like')}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-colors"
            style={{
              background: userReaction === 'like' ? 'var(--primary, #6366f1)' : 'var(--cardHoverBg, #f3f4f6)',
              color: userReaction === 'like' ? '#fff' : 'var(--foreground, #111827)',
              border: '1px solid var(--border, #e5e7eb)',
            }}
            aria-label={t('shared_video.like')}
          >
            <ThumbsUp className="w-4 h-4" aria-hidden="true" />
            <span>{likes}</span>
          </button>

          <button
            type="button"
            onClick={() => toggleReaction('dislike')}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-colors"
            style={{
              background: userReaction === 'dislike' ? 'var(--textSecondary, #6b7280)' : 'var(--cardHoverBg, #f3f4f6)',
              color: userReaction === 'dislike' ? '#fff' : 'var(--foreground, #111827)',
              border: '1px solid var(--border, #e5e7eb)',
            }}
            aria-label={t('shared_video.dislike')}
          >
            <ThumbsDown className="w-4 h-4" aria-hidden="true" />
            <span>{dislikes}</span>
          </button>

          {/* Comments toggle */}
          <button
            type="button"
            onClick={() => setShowComments((v) => !v)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-colors"
            style={{
              background: 'var(--cardHoverBg, #f3f4f6)',
              color: 'var(--foreground, #111827)',
              border: '1px solid var(--border, #e5e7eb)',
            }}
          >
            <MessageSquare className="w-4 h-4" aria-hidden="true" />
            <span>{commentCount} {t('shared_video.comments')}</span>
          </button>

          {/* Copy share link */}
          {shareUrl && (
            <button
              type="button"
              onClick={handleCopy}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-colors ml-auto"
              style={{
                background: copied ? 'var(--attendancePresentBg, #dcfce7)' : 'var(--cardHoverBg, #f3f4f6)',
                color: copied ? 'var(--attendancePresentFg, #16a34a)' : 'var(--foreground, #111827)',
                border: '1px solid var(--border, #e5e7eb)',
              }}
              aria-label={t('my_shares.copy_link', 'Copy share link')}
            >
              {copied ? (
                <Check className="w-4 h-4" aria-hidden="true" />
              ) : (
                <Copy className="w-4 h-4" aria-hidden="true" />
              )}
              {copied ? t('shared_video.link_copied') : t('my_shares.copy_link', 'Copy link')}
            </button>
          )}
        </div>

        {/* Description */}
        {video.description && (
          <p className="text-sm line-clamp-2" style={{ color: 'var(--textSecondary, #6b7280)' }}>
            {richTextToPlain(video.description)}
          </p>
        )}
      </div>

      {/* Comments — owner mode */}
      {showComments && (
        <div
          className="rounded-2xl p-5"
          style={{ background: 'var(--card, #fff)', border: '1px solid var(--border, #e5e7eb)' }}
        >
          <h2
            className="text-base font-semibold mb-4"
            style={{ color: 'var(--cardTitleColor, var(--foreground, #111827))' }}
          >
            {t('shared_video.comments')}
          </h2>
          <Comments
            videoId={video.id}
            playerRef={playerRef}
            isOwner={true}
            onCommentAdded={() => setCommentCount((n) => n + 1)}
            onCommentDeleted={() => setCommentCount((n) => Math.max(0, n - 1))}
          />
        </div>
      )}
    </div>
  );
};

// ─── SharedVideoCard ─────────────────────────────────────────────────────────

interface SharedVideoCardProps {
  video: Video;
  onSelect: (video: Video) => void;
}

const SharedVideoCard: React.FC<SharedVideoCardProps> = ({ video, onSelect }) => {
  const { t } = useTranslation();
  const shareUrl = buildShareUrl(video);
  const [copied, setCopied] = useState(false);
  const [thumbnailFailed, setThumbnailFailed] = useState(false);
  const [durationFromMetadata, setDurationFromMetadata] = useState<number | undefined>(
    resolveInitialDuration(video.duration)
  );

  const handleDurationResolved = useCallback((seconds: number) => {
    setDurationFromMetadata(seconds);
    persistVideoDuration(video.id, seconds);
  }, [video.id]);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success(t('share_link_copied'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  useEffect(() => {
    const fromApi = resolveInitialDuration(video.duration);
    if (fromApi) setDurationFromMetadata(fromApi);
  }, [video.duration]);

  const dateStr = video.createdAt || video.created_at;
  const localDateLabel = formatLocalDateTime(dateStr);
  const videoSrc = toAbsoluteMediaUrl(video.streamUrl || video.url || video.file_url);
  const thumbnailSrc = toAbsoluteMediaUrl(video.thumbnailUrl || video.thumbnail_url || video.thumbnail);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(video)}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSelect(video)}
      className="rounded-xl overflow-hidden cursor-pointer group transition-all"
      style={{
        background: 'var(--card, #fff)',
        border: '1px solid var(--border, #e5e7eb)',
        boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
        transition: 'box-shadow 0.2s, border-color 0.2s',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 16px rgba(0,0,0,0.10)';
        (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--primary, #6366f1)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.boxShadow = '0 1px 4px rgba(0,0,0,0.05)';
        (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--border, #e5e7eb)';
      }}
      aria-label={`${t('my_shares.view_video', 'View shared video')}: ${video.title}`}
    >
      {/* Thumbnail area */}
      <div
        className="relative flex items-center justify-center"
        style={{ aspectRatio: '16/9', background: 'var(--controlsBg, #1e1e2e)', overflow: 'hidden' }}
      >
        {thumbnailSrc && !thumbnailFailed ? (
          <img
            src={thumbnailSrc}
            className="w-full h-full object-cover"
            alt={video.title || t('my_shares.preview', 'Video preview')}
            loading="lazy"
            onError={() => setThumbnailFailed(true)}
          />
        ) : videoSrc ? (
          <video
            src={videoSrc}
            className="w-full h-full object-cover"
            preload="metadata"
            muted
            playsInline
            onLoadedMetadata={(e) => {
              const seconds = parseDurationSeconds((e.currentTarget as HTMLVideoElement).duration);
              if (seconds) handleDurationResolved(seconds);
            }}
            aria-hidden="true"
          />
        ) : (
          <Film className="w-10 h-10 opacity-30" style={{ color: '#fff' }} aria-hidden="true" />
        )}
        {videoSrc ? <DurationProbe src={videoSrc} onDuration={handleDurationResolved} /> : null}
        <div
          className="absolute bottom-2 right-2 text-xs font-mono px-1.5 py-0.5 rounded"
          style={{ background: 'rgba(0,0,0,0.7)', color: '#fff' }}
        >
          {formatDuration(durationFromMetadata)}
        </div>
        {/* Play overlay on hover */}
        <div
          className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ background: 'rgba(0,0,0,0.3)' }}
        >
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center"
            style={{ background: 'var(--primary, #6366f1)' }}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="white" aria-hidden="true">
              <path d="M6 4l12 6-12 6V4z" />
            </svg>
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="p-4">
        <h3
          className="font-semibold text-sm truncate mb-1"
          style={{ color: 'var(--cardTitleColor, var(--foreground, #111827))' }}
        >
          {video.title}
        </h3>

        <div className="flex items-center gap-3 text-xs" style={{ color: 'var(--textSecondary, #6b7280)' }}>
          {(video.views ?? 0) > 0 && (
            <span className="flex items-center gap-1">
              <Eye className="w-3.5 h-3.5" aria-hidden="true" />
              {video.views}
            </span>
          )}
          {dateStr && (
            <span
              className="flex items-center gap-1"
              title={`${localDateLabel} (${BROWSER_TIME_ZONE})`}
            >
              <Clock className="w-3.5 h-3.5" aria-hidden="true" />
              {localDateLabel}
            </span>
          )}
        </div>

        {/* Copy link */}
        {shareUrl && (
          <button
            type="button"
            onClick={handleCopy}
            className="mt-3 w-full flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-medium transition-colors"
            style={{
              background: copied ? 'var(--attendancePresentBg, #dcfce7)' : 'var(--cardHoverBg, #f3f4f6)',
              color: copied ? 'var(--attendancePresentFg, #16a34a)' : 'var(--textSecondary, #6b7280)',
              border: '1px solid var(--border, #e5e7eb)',
            }}
            aria-label={t('my_shares.copy_link', 'Copy share link')}
          >
            {copied ? <Check className="w-3.5 h-3.5" aria-hidden="true" /> : <Copy className="w-3.5 h-3.5" aria-hidden="true" />}
            {copied ? t('shared_video.link_copied') : t('my_shares.copy_link', 'Copy link')}
          </button>
        )}
      </div>
    </div>
  );
};

// ─── MySharesPage ────────────────────────────────────────────────────────────

const MySharesPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [videos, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);

  const fetchSharedVideos = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiError } = await apiClient.listSharedVideos();
      if (apiError) throw apiError;
      setVideos(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load shared videos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSharedVideos();
  }, [fetchSharedVideos]);

  if (selectedVideo) {
    return (
      <VideoOwnerView
        video={selectedVideo}
        onBack={() => setSelectedVideo(null)}
      />
    );
  }

  return (
    <div style={{ padding: '24px 24px 40px', flex: 1, overflow: 'auto' }}>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: 'var(--primary, #6366f1)' }}
        >
          <Share2 className="w-5 h-5" style={{ color: '#fff' }} aria-hidden="true" />
        </div>
        <div>
          <h1
            className="text-lg font-bold"
            style={{ color: 'var(--cardTitleColor, var(--foreground, #111827))' }}
          >
            {t('my_shares.title', 'My Shares')}
          </h1>
          <p className="text-xs" style={{ color: 'var(--textSecondary, #6b7280)' }}>
            {t('my_shares.subtitle', 'Videos you have shared publicly. Click to view and moderate comments.')}
          </p>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-16">
          <div
            className="animate-spin rounded-full h-10 w-10 border-b-2"
            style={{ borderColor: 'var(--primary, #6366f1)' }}
          />
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div
          className="p-4 rounded-xl text-sm text-center"
          style={{ background: 'var(--attendanceAbsentBg, #fef2f2)', color: 'var(--attendanceAbsentFg, #ef4444)' }}
        >
          {error}
          <button
            type="button"
            onClick={fetchSharedVideos}
            className="ml-3 underline"
          >
            {t('my_shares.retry', 'Retry')}
          </button>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && videos.length === 0 && (
        <div
          className="flex flex-col items-center justify-center py-16 gap-4 text-center rounded-2xl"
          style={{ background: 'var(--card, #fff)', border: '1px solid var(--border, #e5e7eb)' }}
        >
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center"
            style={{ background: 'var(--cardHoverBg, #f3f4f6)' }}
          >
            <Share2 className="w-8 h-8" style={{ color: 'var(--textSecondary, #9ca3af)' }} aria-hidden="true" />
          </div>
          <div>
            <p
              className="font-semibold text-base"
              style={{ color: 'var(--cardTitleColor, var(--foreground, #111827))' }}
            >
              {t('my_shares.empty_title', 'No shared videos yet')}
            </p>
            <p
              className="text-sm mt-1"
              style={{ color: 'var(--textSecondary, #6b7280)' }}
            >
              {t('my_shares.empty_hint', 'Share a video from your library and it will appear here.')}
            </p>
          </div>
        </div>
      )}

      {/* Grid */}
      {!loading && !error && videos.length > 0 && (
        <>
          <p className="text-xs mb-4" style={{ color: 'var(--textSecondary, #6b7280)' }}>
            {t('my_shares.count', { count: videos.length, defaultValue: `${videos.length} shared video${videos.length !== 1 ? 's' : ''}` })}
          </p>
          <p className="text-xs mb-4" style={{ color: 'var(--textSecondary, #6b7280)' }}>
            {t('my_shares.timezone_note', { zone: BROWSER_TIME_ZONE, defaultValue: 'Local time zone: {{zone}}' })}
          </p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
              gap: 16,
            }}
          >
            {videos.map((video) => (
              <SharedVideoCard
                key={video.id}
                video={video}
                onSelect={setSelectedVideo}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default MySharesPage;
